import { createListenerMiddleware } from '@reduxjs/toolkit';

import { calculateTotalBet } from '../../utils/bets';
import { getChipIndexFromWeight, selectMaxAffordableChip } from '../../utils/chips';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import type { CoreRootState } from '../CoreStore';
import { chipActions } from '../slices/chips/slice';
import { ChipSelectionOrigin } from '../slices/chips/types';

// This middleware listens for fetchUserInfoAction.fulfilled and selects chip for user on initial game load.
// Depends on fetchUserInfoAction promise, because selectMaxAffordableChip() requires balance data.
export const chipSelectionMiddleware = createListenerMiddleware();

chipSelectionMiddleware.startListening({
  actionCreator: fetchUserInfoAction.fulfilled,
  effect: (action, { dispatch, getState }) => {
    const state = getState() as CoreRootState;
    const selectedChipFromConfig = state.config.playerConfig?.selectedChipIndex;
    const defaultChipWeight = state.config.tableConfig?.defaultChipOrder;
    const chips = state.chips.chips;
    const userSelectedChipWeight = state.chips.selectedChip?.weight;
    const userInfo = state.gameState.userInfo;

    // Scenario 1: New user joins table with default chip set on table config level.
    // Select closest chip to table default chip.
    if (defaultChipWeight && selectedChipFromConfig === undefined && !userSelectedChipWeight) {
      const totalBet = calculateTotalBet(state.bets);
      const defaultChip = chips[getChipIndexFromWeight(defaultChipWeight)];
      const selectedChip = selectMaxAffordableChip(chips, defaultChip, userInfo, totalBet);
      dispatch(chipActions.setSelectedChip({ ...selectedChip, selectedBy: ChipSelectionOrigin.System }));
    }

    // Scenario 2: New user joins table without default chip set.
    // Select lowest chip.
    if (defaultChipWeight === undefined && selectedChipFromConfig === undefined && !userSelectedChipWeight) {
      const totalBet = calculateTotalBet(state.bets);
      const selectedChip = selectMaxAffordableChip(chips, chips[0], userInfo, totalBet);
      dispatch(chipActions.setSelectedChip({ ...selectedChip, selectedBy: ChipSelectionOrigin.System }));
    }

    // Scenario 3: Returning user joins table.
    // Select chip from player config.
    if (selectedChipFromConfig && !userSelectedChipWeight) {
      const selectedChipFromConfigFallback = selectedChipFromConfig <= chips.length ? selectedChipFromConfig : 0; // Fallback for invalid selected chip in player config
      const selectedChip = chips[selectedChipFromConfigFallback];
      dispatch(chipActions.setSelectedChip({ ...selectedChip, selectedBy: ChipSelectionOrigin.PlayerConfig }));
    }
  },
});
